import React, { useEffect, useRef, useState, memo, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import KeyboardEventHandler from 'react-keyboard-event-handler'

import api from '../../../services/api'
import messages from '../../../services/messages'
import { isArray, isFunction, objLength } from '../../../utils/customMethods'

import { useTheme } from '@material-ui/core/styles'
import { setChatKeyword } from '../../../redux/keyword/actions'
import * as dateFns from "date-fns";
import {
  getUserConfig,
  setUserConfig,
  userConfigError,
  resetUserConfig,
} from '../../../redux/userConfiguration/actions'

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Grid,
} from '@material-ui/core'

import {
  CloseRounded as CloseIcon,
  ExpandLessRounded as UpIcon,
  ExpandMoreRounded as DownIcon,
  BookmarkRounded as BookmarkIcon,
  PeopleOutlineRounded as AgentIcon,
} from '@material-ui/icons'

import SearchField from '../../atoms/inputs/searchField'
import Typography from '../../atoms/display/typography'
import EmptyState from '../../atoms/display/emptyState'
import IconButton from '../../atoms/inputs/iconButton'
import ItemListTransfer from '../../atoms/display/itemListTransfer'

import useStyles from './chatTransferStyle'
import { cockpit, cockpitSkills } from '../../../utils/cockpit'
import Demand from '../panels/Demand'

const chatTransfer = (props) => {
  const MAX_CHARACTER = 150
  const MIN_CHARACTER = 20
  const { enqueueSnackbar } = useSnackbar()

  const defaultStatus = {
    loading: false,
    success: false,
    error: false,
    message: '',
  }

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles()
  const theme = useTheme()

  const userConfig = useSelector((state) => state.userConfiguration)
  const user = useSelector((state) => state.user)
  const conversations = useSelector(
    (state) => state.conversations.conversations
  )
  const tabs = useSelector((state) => state.tabs)
  const features = useSelector((state) => state.features)
  const objFeatureTab = useMemo(() => {
    return features?.find((e) => e.name === 'Tab')
  }, [features])

  const [tabValue, setTabValue] = useState(0)
  const [form, setForm] = useState({})
  const [formError, setFormError] = useState({})
  const [activeItem, setActiveItem] = useState({})
  const [selectedSkill, setSelectedSkill] = useState()
  const [data, setData] = useState(null)
  const [searchData, setSearchData] = useState({ skill: '', user: '' })
  const [status, setStatus] = useState({
    get: defaultStatus,
    update: defaultStatus,
  })
  const [tabInfo, setTabInfo] = useState({})
  const [isTabulated, setIsTabulated] = useState(false);
  const [commentary, setCommentary] = useState('');
  const [showTransfer, setShowTransfer] = useState(false);

  const [error, setError] = useState(false);

  const onRequestCancel = useCallback(()=>{
    setCommentary('');
    setShowTransfer(false);
    props.setOpen(false);
  },[props])
 
  // REFS
  const skillRef = useRef()
  const userRef = useRef()

  useEffect(() => {
    getData()
    getUserConf()
    dispatch(setChatKeyword(''))
  }, [props?.chatId])

  useEffect(() => {
    if (status?.get?.success) {
      // TODO: show alert
    } else if (status?.get?.error) {
      // TODO: show alert
    }
  }, [status?.get])

  useEffect(() => {
    let message = status?.update?.message?.[0]?.value

    if (status?.update?.success) {
      enqueueSnackbar(t('toasts.conversationTransferred'), {
        variant: 'success',
      })
      onRequestCancel();
      if (isFunction(props?.onSubmit)) {
        let obj = {
          status: status?.update,
          chatId: props?.chatId,
        }
        props?.onSubmit(obj)
      }
    } else if (status?.update?.error) {
      enqueueSnackbar(
        t(message ? `toasts.${message}` : 'toasts.conversationNotTransferred'),
        {
          variant: 'error',
        }
      )
      onRequestCancel();
    }
  }, [status?.update, onRequestCancel])

  useEffect(() => {
    setSearchData({ skill: '', user: '' })
  }, [tabValue])

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const inputHandle = (event) => {
    event.persist()
    setForm((form) => ({
      ...form,
      [event?.target?.name]: event?.target?.value,
    }))
  }

  const updateStatus = (action, data) => {
    if (action && data)
      setStatus((e) => ({ ...e, [action]: { ...defaultStatus, ...data } }))
  }

  const getData = async () => {
    updateStatus('get', { loading: true })
    try {
      const response = await messages.get(
        `/tech4h-messages/conversation/transfer?id=${props?.chatId}`
      )

      if (response.status === 200) {
        let data = response?.data?.data
        let message = response?.data?.message
        setData(data)
        updateStatus('get', { success: true, message: message })
      } else {
        throw response.data
      }
    } catch (error) {
      let message =
        error?.response?.status === 500
          ? 'Unavailable service'
          : error?.response?.data?.message
      updateStatus('get', { error: true, message: message })
    }
  }

  // TODO: check with backend if this is the better approach. Probably we should store in userConfig only user preferences,
  // not data to fill forms
  const getUserConf = async () => {
    dispatch(getUserConfig())
    try {
      const response = await api.get(`/userconfs`)

      if (response.status === 200) {
        dispatch(setUserConfig(response.data?.data?.userConfiguration))
      } else {
        throw response.data
      }
    } catch (error) {
      let message =
        error?.response?.status === 500
          ? 'Unavailable service'
          : error?.response?.data?.message
      dispatch(userConfigError({ action: 'get_user_config', message: message }))
    }
    dispatch(resetUserConfig('get_user_config'))
  }

  const renderElement = (key, el, type, configId) => (
    <ItemListTransfer
      el={el}
      data={data}
      key={key}
      itemId={el?.id}
      configId={configId}
      text={el?.name}
      type={type}
      quantity={el?.onlineUsers ?? 0}
      online={el?.onlineUsers > 0 && true}
      bookmarked={!!configId}
      active={activeItem?.id === el?.id}
      setSelectedSkill={setSelectedSkill}
      onClick={(selectedSkillId) => {
        let obj =
          activeItem?.id === el?.id
            ? {}
            : {
                route: type,
                id: el?.id,
              }
        setActiveItem(obj)
        setSelectedSkill(selectedSkillId)
      }}
    />
  )

  const renderItemList = (type) => {
    if (type && data) {
      const list = data?.[`${type}s`]

      const config = userConfig?.configs?.find(
        (el) => el.category === `transfer_${type}s`
      )
      const configId = config?.id
      const favorites = config?.info?.[`${type}_id`] || []

      const searchName = searchData?.[type] || null

      let favoriteList = []
      let defaultList = []

      if (searchName) {
        favoriteList = list.filter(
          (e) =>
            favorites.includes(e.id) &&
            e.name.toLowerCase().includes(searchName.toLowerCase())
        )
        defaultList = list.filter(
          (e) =>
            !favorites.includes(e.id) &&
            e.name.toLowerCase().includes(searchName.toLowerCase())
        )
      } else {
        favoriteList = list.filter((e) => favorites.includes(e.id))
        defaultList = list.filter((e) => !favorites.includes(e.id))
      }

      return (
        <>
          {favoriteList?.length > 0 && (
            <>
              <div className={classes.content}>
                <Typography variant="h6">
                  {type === 'skill'
                    ? t('commons.favoriteSkills')
                    : t('commons.favoriteAgents')}
                </Typography>
                <div style={{ width: 5 }} />
                <BookmarkIcon className={classes.bookmarkIcon} />
              </div>
              {favoriteList.map((el, key) =>
                renderElement(key, el, type, configId)
              )}
              <div style={{ height: 20 }} />
            </>
          )}

          <Typography variant="h6">
            {type === 'skill' ? t('commons.skills') : t('commons.agents')}
          </Typography>

          {defaultList?.length > 0 ? (
            defaultList.map((el, key) => renderElement(key, el, type))
          ) : (
            <EmptyState
              text={
                type === 'skill'
                  ? t('commons.skillsEmpty')
                  : t('commons.agentsEmpty')
              }
            >
              {type === 'skill' ? <BookmarkIcon /> : <AgentIcon />}
            </EmptyState>
          )}

          <div style={{ height: 20 }} />
        </>
      )
    }
  }

  const isNullOrWhitespace = (input) => {
    return !input || !input.trim()
  }

  const search = (type, ref) => {
    if (!isNullOrWhitespace(ref.current?.value) && type) {
      const value = ref.current.value
      setSearchData((prev) => ({ ...prev, [type]: value }))
    } else {
      setSearchData((prev) => ({ ...prev, [type]: null }))
    }
  }

  const renderTab = (type) => {
    const inputRef = type === 'Skill' ? skillRef : userRef

    return (
      <>
        <div
          style={{
            backgroundColor: theme.palette.background.paper2,
            padding: 20,
            marginTop: 20,
            width: '100%',
            display: 'flex',
            flexWrap: 'nowrap',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ width: '100%' }}>
            <KeyboardEventHandler
              handleKeys={['enter']}
              onKeyEvent={(key) => {
                if (key === 'enter') {
                  search(type.toLowerCase(), inputRef)
                }
              }}
            >
              <SearchField
                id={`field-search${type}`}
                label={t(`fields.search${type}.label`)}
                placeholder={t(`fields.search${type}.placeHolder`)}
                type="text"
                color="primary"
                inputRef={inputRef}
                variant="outlined"
                name={`search${type}`}
                error={formError?.[`search${type}`] ? true : false}
                helperText={
                  formError?.[`search${type}`] &&
                  t(
                    `fields.search${type}.errors.${
                      formError?.[`search${type}`]
                    }`
                  )
                }
                autoComplete="off"
                shrink={true}
                fullWidth={true}
              />
            </KeyboardEventHandler>
          </div>
        </div>
        <DialogContent>{renderItemList(type.toLowerCase())}</DialogContent>
      </>
    )
  }

  const onChangeCommentary = useCallback((e)=>{
    const inputText = e?.target?.value
    if(inputText.length === MAX_CHARACTER){
      setError(true)
    } else {
      setError(false)
    }

    if (inputText.length < 20 && !/^[a-zA-Z0-9]/.test(inputText) &&  /[\W_]/.test(inputText)) {
      return;
    } else {
      setCommentary(
        !(inputText?.trim()?.length === 0)
          ? inputText
          : inputText?.trim(),
      );
    }
    
  },[setCommentary]);

  const onRequestChatTransfer = useCallback(()=>{
    setShowTransfer((old)=> !old);
  },[setShowTransfer]);

  const renderContent = () => {
    if (tabValue === 0) {
      return renderTab('Skill')
    }
    if (tabValue === 1) {
      return renderTab('User')
    }
  }

  const handleSubmit = async () => {
    if (objLength(activeItem) > 0) {
      updateStatus('update', { loading: true })
      try {
        let { route } = activeItem
        let params = {}
        if (activeItem?.route === 'skill') {
          params = {
            skillId: activeItem.id,
            commentary,
          }
        } else {
          params = {
            userId: activeItem.id,
            skillId: selectedSkill,
            commentary,
          }
        }
        const response = await messages.put(
          `/tech4h-messages/conversation/transfer/${activeItem?.route}?id=${props?.chatId}`,
          params
        )

        if (response.status === 200) {
          const isValid = cockpitSkills.includes(
            conversations?.[props?.chatId]?.skillId
          )

          if (isValid) {
            const body = [
              {
                conversationId: props?.chatId,
                nomeSolicitante: conversations?.[props?.chatId]?.consumer?.name,
                arriving: false,
                matricula: user?.extra_matricula,
              },
            ]

            await cockpit(body)
          }

          let data = response?.data?.data
          let message = response?.data?.message
          setData(data)
          updateStatus('update', { success: true, message: message })
        } else {
          throw response.data
        }
      } catch (error) {
        let message =
          error?.response?.status === 500
            ? 'Unavailable service'
            : error?.response?.data?.message;

        const response = error?.response?.data;
        
        if(typeof response.message === 'string' && response?.skillAvailability != undefined && isFunction(props?.onSetErrosMessages)){
          
          updateStatus('update', { error: false, loading: false });
          const msg = [];

          if(response?.skillsInExceptions || response?.skillsNotInWorkScheludes){
            props.setOpen(false);
            let date = null;
            try {
              date = dateFns.format(new Date(response?.nextOnDate),'dd/MM/yyyy HH:mm');
              date = date.split(' ')
              date = `${date[0]} - ${date[1]}`;
            } catch (error) {
              
            }
            msg.push(`${t(`alerts.SkillInExceptionOrNotInWorkSchudles`)} ${date}`);
          }else if(response?.hasUsersOnline === false){
            props.setOpen(false);
            msg.push(t(`alerts.SkillNotUserOnline`));  
          }  
          props?.onSetErrosMessages(msg);          
        }else{
          updateStatus('update', { error: true, loading: false, message: message })
        }
        
      }
    }
  }

  const renderDemand = () => (
    <div
      style={{
        backgroundColor: theme.palette.background.paper,
        paddingLeft: 20,
        paddingRight: 20,
      }}
    >
      <div
        className={classes.menuBar}
      >
        <Typography variant="body1">{t('commons.registerDemand')}</Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
        </div>
      </div>
        {tabs?.status?.get_tabs?.loading && (
          <div className={classes.loadingSection}>
            <CircularProgress size={25} color="primary" />
          </div>
        )}
        <div className={classes.scroll}>
          <Demand
            chatId={props?.chatId}
            hideButton={false}
            isTabulated={(status) => setIsTabulated(status)}
            maxWidth={390}
            isRefDocument
          />
        </div>
    </div>
  )

  const renderTransfer = () => (
    <>
      {' '}
      <div
        style={{
          backgroundColor: theme.palette.background.paper,
          paddingLeft: 20,
          paddingRight: 20,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          variant="fullWidth"
          textColor="primary"
        >
          <Tab label={t('commons.skills')} />
          <Tab label={t('commons.agents')} />
        </Tabs>
      </div>
      {renderContent()}
      <DialogActions>
        <Button
          size="large"
          color="primary"
          onClick={onRequestCancel}
          disabled={
            status?.get?.loading ||
            status?.update?.loading ||
            userConfig?.status?.get_user_config?.loading ||
            objLength(activeItem) === 0
          }
        >
          {props.disagreeText ? props.disagreeText : t('buttons.cancel')}
        </Button>
        <Button
          size="large"
          variant="contained"
          color="primary"
          disabled={
            status?.get?.loading ||
            status?.update?.loading ||
            userConfig?.status?.get_user_config?.loading ||
            objLength(activeItem) === 0
          }
          onClick={handleSubmit}
        >
          {status?.get?.loading || status?.update?.loading ? (
            <CircularProgress size={25} color="primary" />
          ) : props.agreeText ? (
            props.agreeText
          ) : (
            t('buttons.transfer')
          )}
        </Button>
      </DialogActions>
    </>
  )

  const renderCommentary = ()=>{
    return(
      <>        
      <div
        className={classes.menuBar}
      >
        <Typography variant="body1">
          <div style={{fontWeight: "500", fontSize: '16px'}}>
            {t('commons.registerDemand')}
          </div>
        </Typography>
      </div>
      <DialogContent>       
        <TextField 
          type="text"
          value={commentary}
          id="reasonForTransfer" 
          label={t('commons.reasonForTransfer')}
          onChange={onChangeCommentary} 
          inputProps={{
            maxLength: MAX_CHARACTER,
          }}
          InputLabelProps={{
            shrink: true,
            style: error ? { color: '#F46A6A'} : {}
          }}
          style={ error ? {borderColor: `1px solid '#F46A6A'`} : {}}
          error={error}
          placeholder={t('commons.enterReasonForTransfer')}
          FormHelperTextProps={{ style: { backgroundColor: 'transparent', marginLeft: '0', marginRight: '0' } }}
          helperText={
            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', width: '100%', margin: '12px 0 0 0'}}>
              <span>{t('commons.fillInAtLeast20CharactersToContinue')}</span>
              <span style={error ? {color: '#F46A6A'} : {}}>{commentary.length}/{MAX_CHARACTER}</span>
            </div>
          }
          variant="outlined"
          fullWidth minRows={5} 
          multiline/> 
        
   
      </DialogContent>
      <DialogActions>
        <Button
          size="large"
          color="primary"
          onClick={onRequestCancel}          
        >
          {props.disagreeText ? props.disagreeText : t('buttons.cancel')}
        </Button>
        <Button
          size="large"
          variant="contained"
          color="primary"
          disabled={commentary.length < MIN_CHARACTER}          
          onClick={onRequestChatTransfer}
        >
          {t('buttons.transfer')}
        </Button>
      </DialogActions>
    </>
    );
  }

  const renderModal = useCallback(()=>{
    if(objFeatureTab?.required && !isTabulated)
      return (
        <>
        { renderDemand() }
        </>
      );

    if(isTabulated && !showTransfer)
      return (
        <>
        { renderCommentary() }
        </>
      );
    
    return (
      <>
      { renderTransfer() }
      </>
    );
  },[isTabulated, showTransfer, objFeatureTab, renderDemand, renderCommentary, renderTransfer]);

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.open}
        onClose={onRequestCancel}
        disableEscapeKeyDown={status?.update?.loading}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '15px',
              fontWeight: '700'
            }}
          >
            <div>{t('commons.transferService')}</div>
            {status?.update?.loading ? (
              <></>
            ) : (
              <div>
                <IconButton
                  onClick={onRequestCancel}
                  style={{ color: theme.palette.primary.main }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            )}
          </div>
        </DialogTitle>
        { renderModal() }        
      </Dialog>
    </>
  )
}

export default memo(chatTransfer)
